import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import { measureOpenAiAdsOrderCreated } from "lib/analytics/openai-ads-pixel";
import { useAxios } from "hooks/useAxios";
import { PATH_DASHBOARD, PATH_WORKSPACE, USER_API } from "config";
import { notificationKeys, projectKeys, referralKeys } from "queries";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { handleToast } from "utils/toasts";
import {
  isProjectLicensedFromPayload,
  type ProjectLicenseReadyPayload,
} from "utils/otpPaymentVerification";
import { CheckoutStatusPanel } from "../CheckoutStatusPanel";

interface ProjectPurchaseSuccessResultProps {
  sessionId?: string | null;
  workspaceId?: string | null;
  projectId?: string | null;
}

export const ProjectPurchaseSuccessResult: React.FC<ProjectPurchaseSuccessResultProps> = ({
  sessionId,
  workspaceId,
  projectId,
}) => {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const axios = useAxios();
  const [isApplyingLicense, setIsApplyingLicense] = useState(false);
  const [licenseApplied, setLicenseApplied] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const projectPath = useMemo(() => {
    if (!projectId?.trim() || !workspaceId?.trim()) {
      return null;
    }
    return PATH_WORKSPACE.workspaceProject
      .replace(":workspaceId", workspaceId.trim())
      .replace(":projectId", projectId.trim());
  }, [projectId, workspaceId]);

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.PROJECT_PURCHASE_SUCCESS, {
      value: 1.0,
      currency: "USD",
      transaction_id: sessionId || "",
      project_id: projectId?.trim() || undefined,
      workspace_id: workspaceId?.trim() || undefined,
      context: "hosted_checkout_return",
    });
    measureOpenAiAdsOrderCreated({
      eventId: sessionId?.trim() || undefined,
    });
  }, [trackEvent, sessionId, projectId, workspaceId]);

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: referralKeys.pendingBonus() });
    void queryClient.invalidateQueries({ queryKey: referralKeys.stats() });
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    void queryClient.invalidateQueries({ queryKey: catalogKeys.all });
  }, [queryClient]);

  useEffect(() => {
    const pid = projectId?.trim();
    if (!pid) {
      return;
    }

    let cancelled = false;

    const applyLicenseToProject = async () => {
      setIsApplyingLicense(true);
      try {
        const projectResponse = await axios.get(USER_API.getProject(pid));
        const projectPayload = (projectResponse.data.data ||
          projectResponse.data) as ProjectLicenseReadyPayload;

        if (cancelled) {
          return;
        }

        if (isProjectLicensedFromPayload(projectPayload)) {
          setLicenseApplied(true);
          setAttachError(null);
          return;
        }

        await axios.post(`${USER_API.projects}/${pid}/upgrade`);

        if (cancelled) {
          return;
        }

        setLicenseApplied(true);
        setAttachError(null);
        await queryClient.invalidateQueries({ queryKey: projectKeys.all });
        await queryClient.invalidateQueries({ queryKey: projectKeys.detail(pid) });
        handleToast("License applied to your project.", "success");
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "We could not attach a license to this project yet.";
        setAttachError(message);
        handleToast(message, "error");
      } finally {
        if (!cancelled) {
          setIsApplyingLicense(false);
        }
      }
    };

    void applyLicenseToProject();

    return () => {
      cancelled = true;
    };
  }, [axios, projectId, queryClient]);

  const handleOpenProject = () => {
    if (projectPath) {
      navigate(projectPath);
      return;
    }
    navigate(PATH_DASHBOARD.home);
  };

  const hasProject = Boolean(projectId?.trim());
  const noAvailableLicense =
    Boolean(attachError) && /no available licenses/i.test(attachError ?? "");

  // Payment already succeeded at Stripe — keep a success treatment even if attach is delayed.
  const variant = isApplyingLicense && !licenseApplied && !attachError ? "pending" : "success";

  const title = hasProject
    ? licenseApplied
      ? "Your project is licensed"
      : attachError
        ? "Payment successful"
        : "Purchase successful"
    : "Purchase successful";

  const description = hasProject
    ? licenseApplied
      ? "You’re all set. This project now has a RivetAI license. Keep editing and use the full toolkit."
      : isApplyingLicense
        ? "Thanks for your purchase. We’re applying your license to this project. This usually takes a moment."
        : attachError
          ? noAvailableLicense
            ? "Thanks for your purchase. We’re finishing setup on our side. Your receipt is in Billing; once the license is ready, open the project and apply it (or contact support if it doesn’t appear shortly)."
            : "Thanks for your purchase. We couldn’t attach the license to this project automatically yet. Open Billing to confirm your receipt, then try applying the license from the project."
          : "Thanks for your purchase. Your license is ready. Open the project to continue."
    : "Thanks for your purchase. Your license is ready whenever you create or open a project.";

  return (
    <CheckoutStatusPanel
      variant={variant}
      title={title}
      description={description}
      primaryAction={
        hasProject
          ? attachError
            ? {
                label: "View receipt in Billing",
                onClick: () => navigate(PATH_DASHBOARD.billing),
              }
            : {
                label: isApplyingLicense ? "Applying license…" : "Open project",
                onClick: handleOpenProject,
                disabled: isApplyingLicense,
              }
          : {
              label: "Go to Billing",
              onClick: () => navigate(PATH_DASHBOARD.billing),
            }
      }
      secondaryAction={
        hasProject
          ? attachError
            ? {
                label: "Open project",
                onClick: handleOpenProject,
              }
            : {
                label: "Go to Billing",
                onClick: () => navigate(PATH_DASHBOARD.billing),
              }
          : {
              label: "Go to Home",
              onClick: () => navigate(PATH_DASHBOARD.home),
            }
      }
    />
  );
};
